<template>
  <!-- eslint-disable vue/no-v-html -->
  <v-card flat>
    <p v-if="customQuestions.length === 0" class="mb-0 text-xs-center">
      There are no questions to answer here
    </p>
    <v-card-text v-else class="pa-0">
      <v-form ref="form" @submit.prevent>
        <v-layout id="questionArea" row wrap>
          <v-flex
            v-for="(question, idx) in customQuestionList"
            :key="idx"
            xs12
            md6
            lg3
            pa-2
          >
            <v-tooltip top max-width="400">
              <template v-slot:activator="{ on, attr }">
                <div v-on="on">
                  <v-autocomplete
                    v-if="getType(question.Type) === 'select'"
                    id="Question_1"
                    v-model="question.newValue"
                    :items="optionsMap[question.ID] || []"
                    :loading="!!loadingOptions[question.ID]"
                    :search-input.sync="searchInput['question_' + question.ID]"
                    :rules="getAutoCompleteRules(question)"
                    hide-no-data
                    hide-selected
                    item-text="Description"
                    item-value="API"
                    :label="question.Question"
                    :placeholder="
                      question.newValue ? undefined : 'Start typing to Search'
                    "
                    return-object
                    chips
                    deletable-chips
                    :hint="
                      question.AllowMultiple
                        ? 'You can select multiple answers'
                        : undefined
                    "
                    persistent-hint
                    :multiple="question.AllowMultiple"
                    @input="
                      (v) => {
                        updateValue(question);
                      }
                    "
                  />
                </div>
              </template>
              <span class="break-word">
                {{ question.Question }}
              </span>
            </v-tooltip>
            <v-tooltip top max-width="400">
              <template v-slot:activator="{ on, attr }">
                <div v-on="on">
                  <v-text-field
                    v-if="getType(question.Type) === 'Text'"
                    id="Question_2"
                    v-model="question.newValue"
                    :label="question.Question"
                    :placeholder="question.Question"
                    :type="
                      question.Category == 'Account Versions'
                        ? 'number'
                        : 'text'
                    "
                    :counter="question.CharacterLimit"
                    :maxlength="question.CharacterLimit"
                    :rules="
                      question.Mandatory
                        ? rules.textFilled
                        : rules.notOnlySpaces
                    "
                    @input="updateValue(question)"
                  />
                </div>
              </template>
              <span class="break-word">
                {{ question.Question }}
              </span>
            </v-tooltip>
            <v-tooltip top max-width="400">
              <template v-slot:activator="{ on, attr }">
                <div v-on="on">
                  <v-textarea
                    v-if="getType(question.Type) === 'TextArea'"
                    id="Question_3"
                    v-model="question.newValue"
                    auto-grow
                    :rows="3"
                    :label="question.Question"
                    :placeholder="question.Question"
                    :rules="question.Mandatory ? rules.textFilled : []"
                    @input="updateValue(question)"
                  />
                </div>
              </template>
              <span class="break-word">
                {{ question.Question }}
              </span>
            </v-tooltip>
            <div v-if="getType(question.Type) === 'Checkbox'" class="mr-1">
              <v-input
                id="Question_4"
                class="checkbox_type_question"
                :value="question.newValue"
                :rules="question.Mandatory ? rules.checkBoxRules : []"
                @input="updateValue(question)"
              >
                {{ question.Question }}
              </v-input>
            </div>
            <div
              v-if="getType(question.Type) === 'Checkbox'"
              class="checkboxOptions mr-1"
            >
              <v-checkbox
                v-for="(option, index) in question.Options"
                id="Question_5"
                :key="index"
                v-model="question.checkBoxValue"
                class="sb_checkbox"
                :label="option"
                :value="option"
                @change="
                  (v) => {
                    question.newValue = question.checkBoxValue.join();
                    updateValue(question);
                  }
                "
              />
            </div>
            <div
              v-if="getType(question.Type) === 'Label'"
              class="title"
              v-html="question.Question"
            />
          </v-flex>
        </v-layout>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  getFieldType,
  dependencyChainFulfilled,
  getOptions,
} from '../../../static/QuestionType.js';
import { required } from '../../../static/formRules.js';
import reviewStepBase from './reviewStepBase.mixin.js';
import { fetch } from '../../../utility/fetch.js';

export default {
  name: 'AssetReviewStepCustomQuestions',
  mixins: [reviewStepBase],
  props: {
    validateQuestions: {
      type: Boolean,
    },
    resetValidation: {
      type: Boolean,
    },
  },
  data() {
    return {
      rules: {
        allFilled: [required],
        textFilled: [(v) => (v != null && v.trim().length > 0) || 'Required'],
        checkBoxRules: [(v) => v.length > 0 || 'Required'],
        notOnlySpaces: [
          (v) =>
            v.length == 0 ||
            (v.length != 0 && v.replace(/\s/g, '').length > 0) ||
            'Only spaces are not allowed.',
        ],
      },
      category: '',
      searchInput: {},
      optionsMap: {},
      loadingOptions: {},
    };
  },
  computed: {
    customQuestionList() {
      if (this.customQuestions.length > 0) {
        return this.customQuestions
          .map((cq, idx) => {
            if (!cq.checkBoxValue || cq.checkBoxValue.length == 0) {
              cq.checkBoxValue = [];
            } else if (!Array.isArray(cq.checkBoxValue)) {
              cq.checkBoxValue = cq.checkBoxValue.split(',');
            }
            this.$set(cq, 'originalIndex', idx);
            return cq;
          })
          .filter((cq, idx, allQuestions) => {
            return this.dependencyChainIsFulfilled(cq, allQuestions);
          })
          .sort((a, b) => a.Priority - b.Priority);
      }
      return [];
    },
  },
  watch: {
    validateQuestions() {
      if (this.$refs.form.validate()) {
        this.submitValues();
      }
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    customQuestionList(newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        this.loadSelectOptions();
      }
    },
  },
  async mounted() {
    await this.loadSelectOptions();
  },
  async updated() {
    await this.loadAssetSpecificQuestions();
  },
  methods: {
    dependencyChainIsFulfilled(question, allQuestions) {
      return dependencyChainFulfilled(question, allQuestions);
    },
    getType(type) {
      return getFieldType(type);
    },
    updateValue(question) {
      this.$refs.form.resetValidation();
      this.$refs.form.validate();
      this.$emit(
        'updated',
        'customQuestions',
        question,
        question.originalIndex
      );
      if (this.getType(question.Type) === 'select') {
        this.searchInput['question_' + question.ID] = null;
      }
    },
    submitValues() {
      this.$emit('set-validate', false);
      this.$emit('setQuestionsList', [...this.customQuestionList]);
      this.$emit('keepOnClick');
    },
    getAutoCompleteRules(question) {
      if (!question.Mandatory) {
        return [];
      }
      let rules = [...this.rules.allFilled];
      if (question.AllowMultiple) {
        rules.push((v) => v.length > 0 || 'Required');
      }
      return rules;
    },
    async loadSelectOptions() {
      const fetches = this.customQuestionList
        .filter(cq => this.getType(cq.Type) === 'select' && !cq.Field2 && !this.optionsMap[cq.ID])
        .map(async cq => {
          this.$set(this.loadingOptions, cq.ID, true);
          try {
            const options = await getOptions(cq);
            this.$set(this.optionsMap, cq.ID, options || []);
          } finally {
            this.$set(this.loadingOptions, cq.ID, false);
          }
        });
      await Promise.all(fetches);
    },
    async loadAssetSpecificQuestions() {
      for (let cq of this.customQuestions) {
        if (cq.Field2 !== undefined && cq.Type === 7) {
          const assetId = this.asset._Id;
          const cellValue = await fetch('/Common/GetCellColumnValues', {
            table: cq.Table,
            filterfield: cq.Field,
            reviewfield: cq.Field2,
            id: assetId,
          });
          if (cellValue != '') {
            const splitValues = cellValue.split(cq.Delimiter);
            cq.value = JSON.stringify(splitValues);
            cq.newValue = splitValues;
            this.$set(this.optionsMap, cq.ID, splitValues);
          }
        }
      }
    },
  },
};
</script>
<style>
.checkbox_type_question .v-messages {
  min-height: unset;
}

.checkboxOptions .sb_checkbox {
  margin-top: 8px;
}

.checkboxOptions .sb_checkbox .v-input__slot {
  align-items: start;
}

#questionArea textarea {
  font-size: 14px;
}
</style>
